/**
 * Idempotency keys, one per INTENT rather than one per attempt (PAY-006, F5).
 *
 * The server claims a key before the handler runs and replays the stored
 * response to a retry under the same key; the same key with a DIFFERENT
 * request is refused as a conflict (`request_mismatch`,
 * packages/db/src/idempotency.ts). So the key a browser sends has to follow the
 * request it names:
 *
 * - the same request again - a re-click after the answer was lost - reuses the
 *   key it was first sent under, and replays instead of creating a second
 *   property, batch or invitation;
 * - an edited request is a new intent and gets a new key;
 * - a request that SUCCEEDED releases its key, so doing the same thing again
 *   on purpose is a new intent too.
 *
 * Partner, Internal and the shared mutation hook each minted a fresh key per
 * call, and Partner's `crypto.randomUUID()` threw outright on an http origin.
 * The guest's money keys persist in storage (`idempotencyKeyFor`, PAY-006)
 * because they must survive a reload; they mint through `mintIdempotencyKey`
 * here. The intents held below live in memory: a reload is a new page and the
 * form it shows is a new intent.
 */

/**
 * A fresh key. `crypto.randomUUID` is unavailable on an insecure origin, so a
 * fallback exists rather than a thrown error at the moment of a refund.
 */
export function mintIdempotencyKey(scope = 'cib'): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid !== undefined) return `${scope}-${uuid}`;
  return `${scope}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const held = new Map<string, { readonly fingerprint: string; readonly key: string }>();

/**
 * The key for the intent `scope` names, while what is being sent - the
 * `fingerprint` - is unchanged; a new one the moment it changes.
 */
export function intentKey(scope: string, fingerprint: string): string {
  const current = held.get(scope);
  if (current !== undefined && current.fingerprint === fingerprint) return current.key;
  const key = mintIdempotencyKey(scope.split(':')[0] || 'cib');
  held.set(scope, { fingerprint, key });
  return key;
}

/** The intent was carried out: the next request in this scope is a new one. */
export function clearIntentKey(scope: string): void {
  held.delete(scope);
}

/** What is being sent, as one comparable string. */
export function requestFingerprint(args: unknown): string {
  return JSON.stringify(args ?? null) ?? 'null';
}
